import React from "react";
import PropTypes from "prop-types";
import { Modal, TextArea, Rating, Form, Checkbox } from "semantic-ui-react";
import Api from "../service/Api";
import PromiseButton from "../components/element/PromiseButton";
import ErrorWrapper from "../components/wrapper/ErrorWrapper";
import Analytics from "../service/Analytics";
import ShowOnceWrapper from "../components/wrapper/ShowOnceWrapper";

/**
 * Asks the user for a score and freeform input that is sent to our feedback slack channel
 */
class FeedbackWidget extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      rating: null,
      open: true,
      message: null,
      step: 1,
      immediateResponse: false,
    };
  }

  componentDidMount() {
    this.UNSAFE_componentWillReceiveProps(this.props);
  }

  /* eslint-disable-next-line camelcase */
  UNSAFE_componentWillReceiveProps(props) {
    if (props.hidden) {
      // set by ShowOnceWrapper when user has already seen content before
      this.doCloseModal();
    }
  }

  getCurrentStep() {
    const { step } = this.state;

    return step === 1 ? this.step1() : this.step2();
  }

  step1() {
    const content = (
      <div>
        <p>
          Your feedback is valuable to us! Please let us know what you thought about the functionality you just tried so
          that we can continue to make improvements. For additional help, feel free to contact us at{" "}
          <a href="mailto:hello@tourbuzz.net">hello@tourbuzz.net</a>.
        </p>
        <Form>
          <Form.Field>
            <label htmlFor="rating">Rating</label>
            <Rating
              id="rating"
              maxRating={5}
              onRate={(e, data) => {
                this.state.rating = data.rating;
              }}
            />
          </Form.Field>
          <Form.Field>
            <label htmlFor="thoughts">Your Thoughts</label>
            <TextArea
              id="thoughts"
              onChange={e => {
                this.state.message = e.target.value;
              }}
            />
          </Form.Field>
        </Form>
      </div>
    );

    const actions = <PromiseButton onClick={() => this.submitStep1()} content="Next" color="green" />;

    return {
      content,
      actions,
    };
  }

  step2() {
    const { immediateResponse } = this.state;
    const content = (
      <div>
        <p>Your feedback has been shared with the team, thank you!</p>
        <Form>
          <Form.Field>
            <Checkbox
              label="For a response check here and a member of our support team will be in touch"
              checked={immediateResponse}
              onChange={() => this.setState({ immediateResponse: !immediateResponse })}
            />
          </Form.Field>
        </Form>
      </div>
    );

    const actions = <PromiseButton onClick={() => this.submitStep2()} content="Submit" color="green" />;

    return {
      content,
      actions,
    };
  }

  doCloseModal() {
    this.setState({
      open: false,
    });

    const { onClose } = this.props;

    onClose && onClose();
  }

  submitStep1() {
    const { message, rating } = this.state;
    const { topic, remember } = this.props;

    return Api.post("/feedback/submit", {
      message,
      topic: topic || remember,
      score: rating,
    })
      .then(() => this.setState({ step: 2 }))
      .catch(ex => {
        const { showError } = this.props;
        showError();
        throw ex;
      });
  }

  submitStep2() {
    const { topic, remember } = this.props;
    const { immediateResponse } = this.state;

    return Api.post("/feedback/immediate-response", {
      topic: topic || remember,
      immediateResponse,
    })
      .then(() => this.doCloseModal())
      .catch(ex => {
        const { showError } = this.props;
        showError();
        throw ex;
      });
  }

  render() {
    const { open } = this.state;
    const { shown } = this.props;

    const show = open && shown;

    if (show) {
      Analytics.writeEvent("Feedback Shown");
    }

    const step = this.getCurrentStep();

    return (
      <Modal open={show} size="small" closeIcon="close" onClose={() => this.doCloseModal()}>
        <Modal.Header>Feedback</Modal.Header>
        <Modal.Content>{step.content}</Modal.Content>
        <Modal.Actions>{step.actions}</Modal.Actions>
      </Modal>
    );
  }
}

const showWrap = ShowOnceWrapper(FeedbackWidget);
const errorWrap = ErrorWrapper(showWrap);

export default errorWrap;

FeedbackWidget.propTypes = {
  showError: PropTypes.func,

  // when true, this widget will display a modal if never seen before
  shown: PropTypes.bool,

  // a unique key to use to keep track of whether the user has seen this before
  remember: PropTypes.string,

  // the name to use when sending feedback to server (uses props.remember if not set)
  topic: PropTypes.string,

  // called when the user submits feedback or closes modal
  onClose: PropTypes.func,

  // when true, won't show dialog under any circumstance
  hidden: PropTypes.bool,
};
